import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Carousel } from 'primereact/carousel';
import BaseCard from './BaseCard.jsx';
import IconBriefcase from '../images/IconBriefcase.jsx';
import IconEmail from '../images/IconEmail.jsx';
import IconPhone from '../images/IconPhone.jsx';
import company from '../../resources/images/company.png';
import BaseInput from '../form/BaseInput.jsx';
import IconClear from '../images/IconClear.jsx';
import BaseAutocomplete from '../form/BaseAutocomplete.jsx';
import IconEditCompany from '../images/IconEditCompany.jsx';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useLazyGetCompaniesQuery } from '../../api/contact.js';
import { Link } from 'react-router-dom';

const CompanyCarousel = ({
    className,
    items,
    edit = false,
    control,
    clearErrors,
    removeItem,
    update,
    append,
    addNew,
    setAddNew,
    hideSearch = false
}) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [searchItems, setSearchItems] = useState(
        items.length === 1 &&
            items[0].organization_id === '' &&
            items[0].name === '' &&
            items[0].work_email === '' &&
            items[0].work_phone === '' &&
            items[0].job_title === ''
            ? [0]
            : []
    );
    const [search, setSearch] = useState('');

    const cancelSearch = (index) => {
        setSearchItems((prevItems) => prevItems.filter((item) => item !== index));
        const item = items[index];
        if (
            item.organization_id === '' &&
            item.name === '' &&
            item.work_email === '' &&
            item.work_phone === '' &&
            item.job_title === ''
        ) {
            customRemoveItem(index);
        }
    };

    const changePage = (e) => {
        let newPage = e.page;
        if (newPage > items.length - 1) {
            newPage = items.length - 1;
        }
        if (newPage < 0) {
            newPage = 0;
        }
        setCurrentPage(newPage);
        setSearch('');
    };

    const customRemoveItem = (index) => {
        setSearchItems((prevItems) => prevItems.filter((item) => item !== index));
        removeItem(index);
    };

    const [companiesList, setCompaniesList] = useState([]);
    const [getCompanies, { isLoading: isLoadingCompanies, isError: isErrorCompanies }] = useLazyGetCompaniesQuery();

    const debouncedSearch = useDebounce(search, 500);

    const selectCompany = (id, index) => {
        const company = companiesList.find((item) => item.id === id);

        update(index, {
            organization_id: company.id,
            name: company.name,
            work_email: items[index].work_email,
            work_phone: items[index].work_phone,
            job_title: items[index].job_title
        });

        setCompaniesList([]);
        setSearch('');
        setSearchItems((prevItems) => prevItems.filter((item) => item !== index));
    };

    const createNewCompany = (index) => {
        update(index, {
            organization_id: '',
            name: '',
            work_email: items[index].work_email,
            work_phone: items[index].work_phone,
            job_title: items[index].job_title
        });
        setCompaniesList([]);
        setSearch('');
        setSearchItems((prevItems) => prevItems.filter((item) => item !== index));
    };

    useEffect(() => {
        if (addNew) {
            append({
                organization_id: '',
                name: '',
                work_email: '',
                work_phone: '',
                job_title: ''
            });

            const newPage = items.length;
            setCurrentPage(newPage);
            setSearchItems((prevItems) => [...prevItems, newPage]);
            setAddNew(false);
        }
    }, [addNew, append, items.length, setAddNew]);

    useEffect(() => {
        (async () => {
            if (debouncedSearch !== '') {
                const result = await getCompanies({ search: debouncedSearch });

                setCompaniesList(result.data.response.companies);
            } else {
                setCompaniesList([]);
            }
        })();
    }, [debouncedSearch, getCompanies]);

    useEffect(() => {
        if (hideSearch) {
            setSearchItems((prevItems) => prevItems.filter((item) => item !== 0));
        }
    }, [hideSearch]);

    const companyTemplate = (item) => {
        const index = items.findIndex((obj) => obj.id === item.id);

        return (
            <BaseCard className='mt-0 mb-6 shadow-none'>
                {searchItems.includes(index) ? (
                    <div className='flex justify-between items-center mb-6'>
                        <div className='flex-grow'>
                            <BaseAutocomplete
                                inputClassName='rounded-4xl'
                                items={companiesList.map((company) => ({
                                    label: company.name,
                                    value: company.id
                                }))}
                                onSelect={(id) => selectCompany(id, index)}
                                name='search'
                                value={search}
                                placeholder='Search...'
                                setValue={(value) => setSearch(value)}
                                canAdd
                                onAddNew={() => createNewCompany(index)}
                                isLoading={isLoadingCompanies}
                                exclude={items.map((item) => item.organization_id)}
                                addText='Add new company'
                            />
                        </div>
                        <div className='text-theme1-primary2 cursor-pointer ml-4' onClick={() => cancelSearch(index)}>
                            Cancel
                        </div>
                    </div>
                ) : (
                    <>
                        {edit && removeItem && (
                            <div className='flex justify-end items-center mb-1'>
                                <div
                                    className='cursor-pointer'
                                    onClick={() => setSearchItems((prevItems) => [...prevItems, index])}
                                >
                                    <IconEditCompany className='fill-theme1-disabled mr-2' />
                                </div>
                                <div className='cursor-pointer' onClick={() => customRemoveItem(index)}>
                                    <IconClear className='fill-theme1-disabled' />
                                </div>
                            </div>
                        )}
                        <div className='flex flex-row items-center mb-6 relative'>
                            <div className='mr-6 w-12 h-12 flex items-center justify-center'>
                                <img alt='' src={company} className='w-12 h-auto' />
                            </div>
                            <div className='flex-grow'>
                                <div className='flex items-center'>
                                    {edit && !item.organization_id && (
                                        <BaseInput
                                            inputClassName='p-1 text-theme1-primary font-bold text-xl border-t-0 border-l-0 border-r-0 rounded-none border-b border-theme1-cardStroke'
                                            type='text'
                                            control={control}
                                            value={item.name}
                                            placeholder='Company Name'
                                            name={`companies[${index}].name`}
                                            clearErrors={clearErrors}
                                            errors={[]}
                                        />
                                    )}
                                    {(!edit || item.organization_id) && (
                                        <div className='text-theme1-primary font-bold text-xl'>
                                            <Link to={`/organizations/${item.organization_id}`}>{item.name}</Link>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
                <div className='flex items-center mb-4'>
                    <IconBriefcase className='stroke-theme1-primary mr-4 w-6 h-auto' />
                    {edit && (
                        <BaseInput
                            inputClassName='p-1 border-t-0 border-l-0 border-r-0 rounded-none border-b border-theme1-cardStroke'
                            type='text'
                            control={control}
                            value={item.job_title || ''}
                            placeholder='Work job title'
                            name={`companies[${index}].job_title`}
                            clearErrors={clearErrors}
                            errors={[]}
                        />
                    )}

                    {!edit && (
                        <div
                            className={`w-full py-1 text-sm ${!item.job_title ? 'text-theme1-primary2' : 'text-theme1-text1 font-bold'}`}
                        >
                            {item.job_title || 'Work job title'}
                        </div>
                    )}
                </div>
                <div className='flex items-center mb-4'>
                    <IconEmail className='fill-theme1-primary mr-4 w-6 h-auto' />
                    {edit && (
                        <BaseInput
                            inputClassName='p-1 border-t-0 border-l-0 border-r-0 rounded-none border-b border-theme1-cardStroke'
                            type='text'
                            control={control}
                            value={item.work_email || ''}
                            placeholder='Work email address'
                            name={`companies[${index}].work_email`}
                            clearErrors={clearErrors}
                            errors={[]}
                        />
                    )}

                    {!edit && (
                        <div
                            className={`w-full py-1 text-sm ${!item.work_email ? 'text-theme1-primary2' : 'text-theme1-text1 font-bold'}`}
                        >
                            {item.work_email || 'Work email address'}
                        </div>
                    )}
                </div>
                <div className='flex items-center mb-4'>
                    <IconPhone className='fill-theme1-primary mr-4 w-6 h-auto' />
                    {edit && (
                        <BaseInput
                            inputClassName='p-1 border-t-0 border-l-0 border-r-0 rounded-none border-b border-theme1-cardStroke'
                            type='text'
                            control={control}
                            value={item.work_phone || ''}
                            placeholder='Work phone number'
                            name={`companies[${index}].work_phone`}
                            clearErrors={clearErrors}
                            errors={[]}
                        />
                    )}

                    {!edit && (
                        <div
                            className={`w-full py-1 text-sm ${!item.work_phone ? 'text-theme1-primary2' : 'text-theme1-text1 font-bold'}`}
                        >
                            {item.work_phone || 'Work phone number'}
                        </div>
                    )}
                </div>
            </BaseCard>
        );
    };

    return (
        <div className={twMerge('', className)}>
            <Carousel
                value={items}
                numScroll={1}
                numVisible={1}
                itemTemplate={companyTemplate}
                showNavigators={false}
                circular={false}
                showIndicators
                page={currentPage}
                onPageChange={changePage}
            />
        </div>
    );
};

export default CompanyCarousel;
